import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DotsThree } from '@phosphor-icons/react';
import clsx from 'clsx';

import { openViewAsSession } from '../lib/api';
import { JUSTIFI_PORTAL_URL, TELNYX_PORTAL_URL } from '../lib/organizations';
import type { HealthLevel, OrgRow } from '../lib/types';
import { useDismiss } from './ui';

/** Soft tints only — status chips are tinted, never filled. */
export type PillTone = 'ok' | 'neutral' | 'muted' | 'warn' | 'risk';

const PILL_TONE: Record<PillTone, string> = {
  ok: 'bg-primary/10 text-primary',
  neutral: 'bg-stone-100 text-stone-700',
  muted: 'bg-stone-100 text-stone-500',
  warn: 'bg-orange-50 text-orange-800 ring-1 ring-inset ring-orange-200',
  risk: 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-200',
};

export function Pill({ tone, children, title }: { tone: PillTone; children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className={clsx('inline-block rounded-full px-2 py-0.5 text-xs font-medium leading-snug', PILL_TONE[tone])}
    >
      {children}
    </span>
  );
}

export const HEALTH_LABEL: Record<HealthLevel, string> = {
  healthy: 'Healthy',
  setting_up: 'Setting up',
  watch: 'Watch',
  at_risk: 'At risk',
  lost: 'Lost',
  not_counted: 'Not counted',
};

const HEALTH_TONE: Record<HealthLevel, PillTone> = {
  healthy: 'ok',
  setting_up: 'neutral',
  watch: 'warn',
  at_risk: 'risk',
  lost: 'muted',
  not_counted: 'muted',
};

export function HealthPill({ level }: { level: HealthLevel }) {
  return <Pill tone={HEALTH_TONE[level]}>{HEALTH_LABEL[level]}</Pill>;
}

/** Tone for a vendor status: green when done, orange when the customer or we must act, red when rejected. */
export function vendorTone(bucket: string, waitingOn: string | null): PillTone {
  if (bucket === 'rejected') return 'risk';
  if (bucket === 'live' || bucket === 'own_brand_approved') return waitingOn ? 'warn' : 'ok';
  if (waitingOn === 'customer' || waitingOn === 'us') return 'warn';
  if (bucket === 'not_connected' || bucket === 'not_started') return 'muted';
  return 'neutral';
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export interface CustomerAction {
  key: string;
  label: string;
  /** Why it is unavailable, when it is. */
  disabledReason?: string;
  hint?: string;
  /** Resolves to a line to show the operator, when there is one. */
  run: () => Promise<string | null | void> | string | null | void;
}

/**
 * Everything the operator can do for a company from its row or its account.
 * None of these changes data by itself. View-as opens the office app signed in
 * as the owner with the owner's full permissions — anything done in that tab is
 * real. The rest open a link or copy an ID.
 */
export function useCustomerActions(org: OrgRow): CustomerAction[] {
  const navigate = useNavigate();
  const justifiId = org.justifiSubAccountId ?? org.justifiBusinessId;
  const telnyxId = org.messagingCampaignId ?? org.messagingPhoneNumber;
  return [
    {
      key: 'open',
      label: 'Open account',
      run: () => navigate(`/organizations/${encodeURIComponent(org.orgId)}`),
    },
    {
      key: 'view-as',
      label: 'View as office',
      hint: 'Opens the office app signed in as the owner, in a new tab — anything you change there is real',
      run: async () => {
        await openViewAsSession(org.orgId);
        return 'Opened the office app in a new tab';
      },
    },
    {
      key: 'email',
      label: 'Email the owner',
      disabledReason: org.contactEmail ? undefined : 'No owner email on file',
      run: () => {
        if (org.contactEmail) window.location.href = `mailto:${org.contactEmail}`;
      },
    },
    {
      key: 'justifi',
      label: 'Open in JustiFi',
      hint: justifiId ? 'Copies their JustiFi ID, then opens JustiFi' : undefined,
      disabledReason: justifiId ? undefined : 'Not connected to JustiFi',
      run: async () => {
        if (!justifiId) return null;
        const copied = await copyText(justifiId);
        window.open(JUSTIFI_PORTAL_URL, '_blank', 'noopener');
        return copied ? 'JustiFi ID copied — paste it into JustiFi search' : null;
      },
    },
    {
      key: 'telnyx',
      label: 'Open in Telnyx',
      hint: telnyxId ? 'Copies their campaign ID or number, then opens Telnyx' : undefined,
      disabledReason: telnyxId ? undefined : 'No texting number or campaign yet',
      run: async () => {
        if (!telnyxId) return null;
        const copied = await copyText(telnyxId);
        window.open(TELNYX_PORTAL_URL, '_blank', 'noopener');
        return copied ? 'Telnyx ID copied — paste it into Telnyx search' : null;
      },
    },
    {
      key: 'copy-id',
      label: 'Copy company ID',
      run: async () => ((await copyText(org.orgId)) ? 'Company ID copied' : 'Could not copy'),
    },
  ];
}

/** The "•••" menu on each row. Positioned fixed so the table's scroll box never clips it. */
export function CustomerActionsMenu({ org }: { org: OrgRow }) {
  const actions = useCustomerActions(org);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(rootRef, open, close);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const place = () => {
      const rect = buttonRef.current!.getBoundingClientRect();
      const menuHeight = 280;
      const below = rect.bottom + 6;
      const top = below + menuHeight > window.innerHeight ? Math.max(8, rect.top - menuHeight - 6) : below;
      setPosition({ top, right: Math.max(8, window.innerWidth - rect.right) });
    };
    place();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  const run = async (action: CustomerAction) => {
    setOpen(false);
    try {
      const result = await action.run();
      if (typeof result === 'string') setMessage(result);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'That did not work');
    }
    window.setTimeout(() => setMessage(null), 3000);
  };

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Actions for ${org.name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          'inline-flex h-8 w-8 items-center justify-center rounded-md text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-800',
          open && 'bg-stone-100 text-stone-800',
        )}
      >
        <DotsThree className="h-5 w-5" weight="bold" />
      </button>
      {open && position && (
        <div
          role="menu"
          style={{ top: position.top, right: position.right }}
          className="fixed z-50 flex w-60 flex-col rounded-xl border border-stone-200 bg-white p-1.5 shadow-hero"
        >
          {actions.map((action, index) => (
            <div key={action.key}>
              {index === 3 && <div className="mx-1.5 my-1 h-px bg-stone-100" />}
              <button
                type="button"
                role="menuitem"
                disabled={Boolean(action.disabledReason)}
                title={action.disabledReason ?? action.hint}
                onClick={() => void run(action)}
                className="flex w-full flex-col rounded-md px-2.5 py-2 text-left text-sm text-stone-800 transition-colors hover:bg-stone-50 disabled:cursor-not-allowed disabled:text-stone-400 disabled:hover:bg-transparent"
              >
                <span className="font-medium">{action.label}</span>
                {action.disabledReason && <span className="text-xs text-stone-400">{action.disabledReason}</span>}
              </button>
            </div>
          ))}
        </div>
      )}
      {message && (
        <div
          role="status"
          className="fixed bottom-4 right-4 z-50 max-w-xs rounded-md bg-stone-900 px-3 py-2 text-sm text-white shadow-hero"
        >
          {message}
        </div>
      )}
    </div>
  );
}
